import { Delivery } from "types/Delivery";
import { Reservation } from "services/reservations.service";
import {
  OrderProductGroupLine,
  SummaryRowBase,
  orderLineRows,
  rowOfItem,
} from "./orderLineRows";

export interface FulfillmentRow extends SummaryRowBase {
  reserved: number;
  // Total confirmed-reservation quantity for this row, unclamped by
  // fulfilment (unlike `reserved`, which is what's left to ship) — used
  // only to flag when shipped has run past what was reserved.
  reservedQuantity: number;
  // Shipped against confirmed reservations only (fulfilledQuantity) —
  // compared with reservedQuantity, so trucks loaded by contract
  // without a reservation don't read as "over reserved".
  shippedReserved: number;
  pending: number; // draft/planned deliveries
  shipped: number;
}

const NOTHING_MOVED = {
  reserved: 0,
  reservedQuantity: 0,
  shippedReserved: 0,
  pending: 0,
  shipped: 0,
};

// One row per product (and per product-group line): what the order asks for,
// what is reserved for it, what is on trucks that have not left yet, and what
// has shipped. Reservation and delivery items count on the order line they were
// booked against (see orderLineRows.ts).
export function buildFulfillmentSummary(
  orderItems: any[],
  productGroups: OrderProductGroupLine[],
  reservations: Reservation[],
  deliveries: Delivery[]
): FulfillmentRow[] {
  const summary: Record<string, FulfillmentRow> = {};
  const { rows, rowKeyByLine } = orderLineRows(orderItems, productGroups);
  rows.forEach((row) => {
    summary[row.key] = { ...row, ...NOTHING_MOVED };
  });

  // Sum reserved quantities from confirmed reservations
  reservations.forEach((reservation) => {
    if (reservation.status === "confirmed") {
      (reservation.items || []).forEach((item: any) => {
        const { rowKey, fallbackRow } = rowOfItem(item, rowKeyByLine);
        const quantity = item.quantity || 0;
        const fulfilledQty = item.fulfilledQuantity || 0;
        const remainingReserved = Math.max(0, quantity - fulfilledQty);

        if (!summary[rowKey]) {
          summary[rowKey] = { ...fallbackRow, ...NOTHING_MOVED };
        }
        summary[rowKey].reserved += remainingReserved;
        summary[rowKey].reservedQuantity += quantity;
        summary[rowKey].shippedReserved += fulfilledQty;
        summary[rowKey].shipped += fulfilledQty;
      });
    }
  });

  // Sum shipped quantities from legacy deliveries without reservation links
  deliveries.forEach((delivery) => {
    if (delivery.status === "shipped" || delivery.status === "delivered") {
      (delivery.items || []).forEach((item) => {
        if (item.reservationItem) return;
        const { rowKey, fallbackRow } = rowOfItem(item, rowKeyByLine);

        if (!summary[rowKey]) {
          summary[rowKey] = { ...fallbackRow, ...NOTHING_MOVED };
        }
        summary[rowKey].shipped += item.quantity || 0;
      });
    }
  });

  // Sum pending quantities from draft/planned deliveries
  deliveries.forEach((delivery) => {
    if (delivery.status === "draft" || delivery.status === "planned") {
      (delivery.items || []).forEach((item) => {
        const { rowKey } = rowOfItem(item, rowKeyByLine);

        if (summary[rowKey]) {
          summary[rowKey].pending += item.quantity || 0;
        }
      });
    }
  });

  return Object.values(summary);
}
